// Product templates: bind a concept to a provider blueprint with printable
// area, safe area, and handle zones; review it; and browse versions. Production
// promotion + print QA happens from the artwork detail page.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type { Concept, ProductTemplate } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

const SAMPLE_TEMPLATE = {
  name: 'Reading Circle Mug 11oz',
  provider: 'printify',
  productFamily: 'mug',
  blueprintId: 'blueprint-9001',
  providerTemplateVersion: '2026-08',
  retrievedAt: '2026-08-05T00:00:00.000Z',
  printableArea: { x: 150, y: 80, width: 1300, height: 740 },
  safeArea: { x: 200, y: 100, width: 1200, height: 700 },
  bleedPx: 24,
  handleZones: [
    { x: 0, y: 0, width: 150, height: 900 },
    { x: 1450, y: 0, width: 150, height: 900 },
  ],
  blankColor: '#ffffff',
  outputDimensions: { width: 1600, height: 900 },
  feasibilityConfirmed: false,
  feasibilityNote: 'Attach a dated Printify blueprint snapshot before implying feasibility.',
};

export function ProductTemplatesPage() {
  const queryClient = useQueryClient();
  const [conceptId, setConceptId] = useState('');
  const [json, setJson] = useState(JSON.stringify(SAMPLE_TEMPLATE, null, 2));
  const [error, setError] = useState<string | null>(null);

  const conceptsQuery = useQuery({
    queryKey: ['concepts', 'approved'],
    queryFn: () => api.get<Concept[]>('/concepts?status=approved&pageSize=100'),
  });
  const listQuery = useQuery({
    queryKey: ['product-templates'],
    queryFn: () => api.get<ProductTemplate[]>('/product-templates?pageSize=100'),
  });

  const create = useMutation({
    mutationFn: () => {
      if (!conceptId) throw new Error('Select an approved concept');
      let body: Record<string, unknown>;
      try {
        body = JSON.parse(json);
      } catch {
        throw new Error('Template must be valid JSON');
      }
      return api.post('/product-templates', { ...body, conceptId });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['product-templates'] });
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : String(e)),
  });

  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'archived' }) =>
      api.post(`/product-templates/${id}/review`, { decision }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['product-templates'] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : String(e)),
  });

  const concepts = conceptsQuery.data?.data ?? [];
  const templates = listQuery.data?.data ?? [];

  return (
    <section>
      <h1>Product Templates</h1>
      <p className="muted">
        Bind a concept to a provider blueprint with printable-area and handle-zone geometry.
        Feasibility is never implied without a dated owner snapshot.
      </p>

      <div className="card">
        <h2>New template</h2>
        <div className="form-row">
          <label htmlFor="pt-concept">Approved concept</label>
          <select id="pt-concept" value={conceptId} onChange={(e) => setConceptId(e.target.value)}>
            <option value="">Select a concept…</option>
            {concepts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="pt-json">Template (JSON)</label>
          <textarea
            id="pt-json"
            rows={22}
            className="mono"
            value={json}
            onChange={(e) => setJson(e.target.value)}
          />
        </div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button type="button" onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create template'}
        </button>
      </div>

      <div className="card">
        <h2>Templates</h2>
        {templates.length === 0 && <p className="muted">None yet.</p>}
        <table className="list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
              <th>Provider</th>
              <th>Output</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>v{t.version}</td>
                <td>{t.provider}</td>
                <td>
                  {t.outputDimensions.width}×{t.outputDimensions.height}
                </td>
                <td>
                  <StatusBadge status={t.status} />
                </td>
                <td>
                  {t.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => review.mutate({ id: t.id, decision: 'approved' })}
                    >
                      Approve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
